// components/auth/ProtectedRoute.js - Enhanced with session validation
import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { sessionManager } from '../../../shared/services/sessionManagement/sessionManager';
import { useUser } from '../hooks/useUser';
import PostLoginLoader from './PostLoginLoader';
import DivisionSwitchLoader from '../../organization/components/DivisionSwitchLoader';
import { DIVISION_SWITCH_STORAGE_KEY } from '../../organization/utils/switchDivisionSession';

// Read once at module scope, not per-render — this only matters for the very first render
// right after switchDivisionSession's hard reload, and AppSuspenseFallback (App.js's own
// Suspense boundary) owns clearing the sessionStorage flag once the page has actually booted.
const getDivisionSwitchName = () => {
  try {
    return sessionStorage.getItem(DIVISION_SWITCH_STORAGE_KEY);
  } catch (error) {
    return null;
  }
};

export const ProtectedRoute = () => {
  const { isAuthenticated, isHydrating } = useUser();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Wait for user data hydration before making auth decisions —
    // otherwise the deferred JSON.parse hasn't completed yet and
    // the user would incorrectly appear unauthenticated.
    if (isHydrating) return;

    const validateAccess = () => {
      // Check user authentication
      const userAuth = isAuthenticated();

      // Check session validity
      const sessionValid = sessionManager.isAuthenticated();

      const isAccessValid = !!(userAuth && sessionValid);

      setIsValid(isAccessValid);
      setIsValidating(false);

      // If not valid, session manager will handle the redirect
      if (!isAccessValid && sessionValid === false) {
        // Session expired, let sessionManager handle it
        return;
      }
    };

    validateAccess();

    // Listen for session expiry
    const handleSessionExpired = () => {
      setIsValid(false);
      setIsValidating(false);
    };

    // Organisation suspended mid-session — stash the details for HomeScreen to
    // pick up after the redirect, then treat it like any other session expiry
    const handleOrganizationSuspended = async (event) => {
      try {
        localStorage.setItem('orgSuspendedInfo', JSON.stringify(event.detail || {}));
      } catch {
        // Non-blocking — worst case the suspended screen shows without org details
      }
      await sessionManager.clearSession();
      setIsValid(false);
      setIsValidating(false);
    };

    window.addEventListener('sessionExpired', handleSessionExpired);
    window.addEventListener('organizationSuspended', handleOrganizationSuspended);

    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpired);
      window.removeEventListener('organizationSuspended', handleOrganizationSuspended);
    };
  }, [isAuthenticated, isHydrating, location]);

  if (isValidating) {
    // Right after switchDivisionSession's hard reload, this mounts before App.js's own
    // Suspense boundary is even reached — its hardcoded PostLoginLoader would flash in
    // ahead of AppSuspenseFallback's own check, defeating the point of that fix. Same flag,
    // same fallback choice.
    const divisionSwitchName = getDivisionSwitchName();
    return divisionSwitchName
      ? <DivisionSwitchLoader divisionName={divisionSwitchName} activeStep={3} />
      : <PostLoginLoader />;
  }

  return isValid ? <Outlet /> : <Navigate to="/" state={{ from: location }} replace />;
};